import React, { useState } from 'react';
import { Button, Col, Container, Form, Modal, Row } from 'react-bootstrap';
import PhoneInput from 'react-phone-number-input';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import validator from 'validator';
import { postRequest } from '../../helpers/apiHandler';
import { getTimezone } from '../../helpers/helperFunction';
import CloseIcon from '../../layouts/SVG/CloseIcon';

const white_label_perkeso = process.env.REACT_APP_WHITE_LABEL_PERKESO === 'true';

const AddUserModal = ({ show, onClose, setAlertState, refreshData, countryOptions }) => {
  const { access_token, role } = useSelector((state) => state.auth.user);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [selected_country, setSelected_country] = useState(
    white_label_perkeso
      ? { value: "MY", label: "Malaysia (MY)" }
      : { value: null, label: null }
  );
  const [timezone, setTimezone] = useState(white_label_perkeso
    ? { value: "Asia/Kuala_Lumpur", label: "Asia/Kuala Lumpur (GMT+08:00)" }
    : null
  );
  const [currency, setCurrency] = useState('MYR');
  const [userRole, setUserRole] = useState('user');

  const clearData = () => {
    setName('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setSelected_country({ value: null, label: null });
    setTimezone(null);
    setCurrency('MYR');
    setUserRole('user');
    onClose();
  };

  const handleSubmit = () => {
    if (!name || validator.isEmpty(name)) {
      setAlertState({
        message: 'Please enter user name',
        show: true,
        variant: 'danger',
      });
      return;
    }

    if (!validator.matches(name, /^[a-zA-Z ]+$/)) {
      setAlertState({
        message: 'User name must not contain numbers.',
        show: true,
        variant: 'danger',
      });
      return;
    }

    if (!email || validator.isEmpty(email)) {
      setAlertState({
        message: 'Please enter user email address',
        show: true,
        variant: 'danger',
      });
      return;
    }

    if (!validator.isEmail(email)) {
      setAlertState({
        message: 'Please enter valid email address',
        show: true,
        variant: 'danger',
      });
      return;
    }

    if (!password || validator.isEmpty(password)) {
      setAlertState({
        message: 'Please enter your password',
        show: true,
        variant: 'danger',
      });
      return;
    }

    if ( white_label_perkeso && !validator.matches(
          password,
          /(?=[A-Za-z0-9@#$%^&+!=]+$)^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[@#$%^&+!=]).*$/g))
    {
      setAlertState({
        message:
          'Please choose a strong password that includes at least 1 lowercase and uppercase letter, a number, as well as a special character (!@#$%^&*)',
        show: true,
        variant: 'danger',
      });
      return;
    }

    const min_pwd_length = white_label_perkeso ? 12 : 8;
    if (!validator.isLength(password, { min: min_pwd_length, max: undefined })) {
      setAlertState({
        message: `The password must be at least ${min_pwd_length} characters long.`,
        show: true,
        variant: 'danger',
      });
      return;
    }

    if (!confirmPassword || validator.isEmpty(confirmPassword)) {
      setAlertState({
        message: 'Please confirm your password',
        show: true,
        variant: 'danger',
      });
      return;
    }

    if (password !== confirmPassword) {
      setAlertState({
        message: 'Password do not match',
        show: true,
        variant: 'danger',
      });
      return;
    }

    if (!timezone || validator.isEmpty(timezone.value)) {
      setAlertState({
        message: 'Please select the timezone',
        show: true,
        variant: 'danger',
      });
      return;
    }

    if (!currency || validator.isEmpty(currency)) {
      setAlertState({
        message: 'Please select the currency',
        show: true,
        variant: 'danger',
      });
      return;
    }

    if (!phone || validator.isEmpty(phone)) {
      setAlertState({
        message: 'Please select the mobile number.',
        show: true,
        variant: 'danger',
      });
      return;
    }
    createUser();
  };

  const createUser = () => {
    const body = {
      name,
      email,
      password,
      country: selected_country,
      timezone,
      currency,
      phone_number: phone,
      user_role: userRole,
    };

    postRequest(`/admin/user-management/create`, body, access_token.token, (res) => {
      if (res.success) {
        refreshData();
        clearData();
        setAlertState({
          message: 'User added successfully. An email invitation has been sent to the user',
          show: true,
          variant: 'success',
        });
      } else {
        if (res.code === 1037) {
          setAlertState({
            message: res.data.email + ' already exist',
            show: true,
            variant: 'warning',
          });
          return;
        }
        setAlertState({
          message:
            'Unable to add new user. Please try again or contact support if the issue persist',
          show: true,
          variant: 'danger',
        });
      }
    });
  };

  return (
    <Modal show={show} onHide={clearData} size="lg">
      <Modal.Body>
        <CloseIcon handleModalClose={clearData} />
        <Modal.Title as="h4" style={{ margin: '8px 0 16px' }}>Add User</Modal.Title>
        <Form>
          <Container>
            <Row>
              <Col>
                <Form.Group>
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    type="input"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col>
                <Form.Group>
                  <Form.Label>Password</Form.Label>
                  <Form.Control
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group>
                  <Form.Label>Confirm Password</Form.Label>
                  <Form.Control
                    type="password"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="my-2">
              <Col>
                <Form.Group>
                  <Form.Label>Mobile Number</Form.Label>
                  <PhoneInput
                    className="form-control"
                    international
                    defaultCountry="MY"
                    value={phone}
                    onChange={(e) => setPhone(e)}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group>
                  <Form.Label>Country</Form.Label>
                  <Select
                    name="country"
                    options={countryOptions}
                    value={selected_country}
                    onChange={(value) => {
                      console.log(value);
                      setSelected_country(value);
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col sm={6}>
                <Form.Group>
                  <Form.Label>Time Zone</Form.Label>
                  <Select
                    name="timezone"
                    options={getTimezone()}
                    value={timezone}
                    onChange={(value) => {
                      console.log(value);
                      setTimezone(value);
                    }}
                  />
                </Form.Group>
              </Col>
              {!white_label_perkeso && (
                <Col sm={6}>
                  <Form.Group>
                    <Form.Label>Default currency</Form.Label>
                    <Form.Control
                      as="select"
                      name="currency"
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value)}
                    >
                      <option value="USD">United States Dollars</option>
                      <option value="EUR">Euro</option>
                      <option value="MYR">Malaysia Ringgit</option>
                    </Form.Control>
                  </Form.Group>
                </Col>
              )}
              {role === 'admin' && (
                  <Col sm={6}>
                    <Form.Group>
                      <Form.Label>Role</Form.Label>
                      <Form.Control
                        as="select"
                        name="role"
                        value={userRole}
                        onChange={(e) => setUserRole(e.target.value)}
                      >
                        <option value="user">User</option>
                        <option value="manager">Manager</option>
                      </Form.Control>
                    </Form.Group>
                  </Col>
              )}
            </Row>
          </Container>
        </Form>
        <Form.Group className="d-flex justify-content-start" style={{ marginLeft: 16, marginTop: 24, gap: 16 }}>
        <Button variant="primary" onClick={handleSubmit} style={{ width: "140px" }}>
            {'Save'}
          </Button>
          <Button variant="ghost" onClick={clearData} style={{ width: "140px" }}>
            Cancel
          </Button>
        </Form.Group>
      </Modal.Body>
    </Modal>
  );
};

export default AddUserModal;
